/* eslint-disable react/jsx-props-no-spreading */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { withStyles, withTheme } from '@glu/theming';
import {
  CheckmarkCircleIcon,
  CaretRightIcon,
  CaretDownIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InfoCircleIcon
} from '@glu/icons-react/lib';

import withLocale from 'components/withLocale/withLocale';
import AppPropTypes from 'proptypes/AppPropTypes';

import { JSSClasses } from 'proptypes/jss-prop-types';
import GrowY from 'components/transitions/GrowY';
import Fade from 'components/transitions/Fade';
import styles from './Alert.styles';

export class AlertComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      display: false,
      visible: true
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { variant } = nextProps;
    const ERROR = variant === 'error';
    const WARNING = variant === 'warning';

    return {
      variant,
      display: prevState.variant !== variant ? ERROR || WARNING : prevState.display,
      visible: prevState.visible
    };
  }

  buildGluClasses = () => {
    const { className, classes, dismissible, variant } = this.props;
    const names = {};

    return classnames(
      'alert',
      className,
      classes.alert,
      classes[variant],
      names[variant] || `alert-${variant}`,
      {
        'alert-dismissible': dismissible
      }
    );
  };

  displayChildren = () => {
    const showDetails = !this.props.details;
    const isVisible = this.state.display;
    const isExpanded = this.props.expanded;

    return showDetails || isVisible || isExpanded || false;
  };

  /* eslint-disable react/sort-comp */
  alertRef = React.createRef();

  componentDidMount() {
    const { autoCloseTime, autoFocus, onHide } = this.props;
    if (this.alertRef.current !== null && autoFocus) {
      this.focus();
    }
    if (autoCloseTime) {
      this.timer = setTimeout(() => onHide(), autoCloseTime);
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  close = (event) => {
    if (this.props.onHide) {
      return this.props.onHide(event);
    }

    return this.setState({ visible: !this.state.visible });
  };

  focus = () => {
    this.alertRef.current.focus();
  };

  toggleDisplay = () => {
    this.setState((state) => ({ display: !state.display }));
  };

  getIcon = () => {
    switch (this.props.variant) {
      case 'success':
        return <CheckmarkCircleIcon />;
      case 'error':
        return <ExclamationCircleIcon />;
      case 'warning':
        return <ExclamationTriangleIcon />;
      default:
        return <InfoCircleIcon />;
    }
  };

  render() {
    const {
      autoCloseTime,
      basedOn,
      children,
      classes: {
        caretWrapper,
        checkmarkWrapper,
        close,
        guidMsg,
        inline,
        message,
        preventOverflow
      },
      clsChild,
      clsContent,
      dataQa,
      details,
      dismissible,
      errorGuid,
      expanded,
      hideAccordion,
      in: show,
      locale,
      mountOnEnter,
      msg,
      onHide,
      suppressIcon,
      title,
      unmountOnExit,
      variant,
      ...rest
    } = this.props;

    const { display, visible } = this.state;

    const transitionProps = {
      in: show,
      mountOnEnter,
      unmountOnExit
    };

    if (!basedOn || !visible) {
      return null;
    }

    return (
      <GrowY {...transitionProps}>
        <div
          data-qa={dataQa ? `${dataQa}-${variant}-alert` : `${variant}-alert`}
          ref={this.alertRef}
          role="alert"
          /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
          tabIndex="0"
          {...rest}
          className={this.buildGluClasses()}
        >
          {suppressIcon ? null : (
            <div className={classnames(inline, checkmarkWrapper)}>{this.getIcon()}</div>
          )}
          {dismissible && (
            <div className={close}>
              <XCircleIcon onClick={this.close} data-qa="alert-close" />
            </div>
          )}
          <div className={classnames(inline, preventOverflow, { alert__content: clsContent })}>
            {details && (
              <div className={classnames(caretWrapper)}>
                {!hideAccordion && (
                  <div className={inline} id="expandCollapse">
                    {!display && !expanded ? (
                      <CaretRightIcon onClick={this.toggleDisplay} />
                    ) : (
                      <CaretDownIcon onClick={this.toggleDisplay} />
                    )}
                  </div>
                )}
                <div className={inline}>{title && <h3>{title}</h3>}</div>
              </div>
            )}
            {this.displayChildren() && (
              <Fade {...transitionProps}>
                <>
                  {msg || null}
                  <div className={classnames({ [clsChild]: clsChild, [message]: details })}>
                    {children}
                  </div>
                  {errorGuid && (
                    <div className={guidMsg}>
                      <span>
                        {locale['common.referenceNumber']} - {errorGuid}
                      </span>
                    </div>
                  )}
                </>
              </Fade>
            )}
          </div>
        </div>
      </GrowY>
    );
  }
}

AlertComponent.propTypes = {
  autoFocus: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.func])),
    PropTypes.element,
    PropTypes.node,
    PropTypes.func
  ]),
  classes: JSSClasses,
  className: PropTypes.string,
  details: PropTypes.bool,
  dismissible: PropTypes.bool,
  errorGuid: PropTypes.string,
  mountOnEnter: PropTypes.bool,
  msg: PropTypes.string,
  onHide: PropTypes.func,
  in: PropTypes.bool,
  suppressIcon: PropTypes.bool,
  title: PropTypes.node,
  unmountOnExit: PropTypes.bool,
  variant: PropTypes.string,
  expanded: PropTypes.bool,
  basedOn: PropTypes.bool,
  autoCloseTime: PropTypes.number,
  clsContent: PropTypes.bool,
  clsChild: PropTypes.string,
  hideAccordion: PropTypes.bool,
  dataQa: PropTypes.string,
  locale: AppPropTypes.locale
};

AlertComponent.defaultProps = {
  children: [],
  classes: {},
  className: '',
  details: false,
  dismissible: true,
  errorGuid: '',
  mountOnEnter: true,
  msg: '',
  onHide: null,
  in: true,
  suppressIcon: false,
  title: '',
  unmountOnExit: true,
  variant: 'info',
  expanded: false,
  basedOn: true,
  autoCloseTime: null,
  clsContent: false,
  clsChild: '',
  hideAccordion: false,
  dataQa: null,
  locale: {},
  autoFocus: true
};

const AlertStyled = withStyles(styles)(AlertComponent);
const AlertLocaled = withLocale(AlertStyled);
export default withTheme(AlertLocaled);
